import { useState } from 'react';
import type { Invoice } from '@freelancer-os/shared-types';
import { Badge } from '@freelancer-os/ui/src/components/ui/badge';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@freelancer-os/ui/src/components/ui/card';
import { Separator } from '@freelancer-os/ui/src/components/ui/separator';
import { ArrowLeft, Bell, Download, Edit, ExternalLink, Send, Trash, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDeleteInvoice, useInvoice } from '../../hooks/useInvoices';
import { RecordPaymentDrawer } from '../payments/components/RecordPaymentDrawer';
import { SendInvoiceDialog } from './components/SendInvoiceDialog';

export function InvoiceDetailPage() {
  const { t } = useTranslation('invoices');
  const { id } = useParams();
  const invoiceId = id ?? null;
  const navigate = useNavigate();

  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const { mutate: deleteInvoice, isPending: isDeleting } = useDeleteInvoice();
  const [sendOpen, setSendOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  if (isLoading) {
    return <div className="p-8 max-w-4xl mx-auto">{t('detail.loading')}</div>;
  }

  if (!invoice) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-4">{t('detail.notFound')}</h1>
        <Button variant="outline" onClick={() => navigate('/invoices')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> {t('detail.backToList')}
        </Button>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm(t('detail.deleteConfirm'))) {
      deleteInvoice(invoice.id, {
        onSuccess: () => navigate('/invoices'),
      });
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

  const getStatusBadge = (status: Invoice['status']) => {
    const label = t(`status.${status}`);
    switch (status) {
      case 'DRAFT':
        return <Badge variant="secondary">{label}</Badge>;
      case 'SENT':
        return <Badge className="bg-blue-500">{label}</Badge>;
      case 'PAID':
        return <Badge className="bg-green-500">{label}</Badge>;
      case 'OVERDUE':
        return <Badge variant="destructive">{label}</Badge>;
      case 'CANCELLED':
        return <Badge variant="outline">{label}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto w-full">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/invoices')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('detail.backToList')}
          </Button>
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold tracking-tight">{t('detail.invoiceTitle', { code: invoice.invoiceCode })}</h1>
            {getStatusBadge(invoice.status)}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a
              href={`${import.meta.env.VITE_WEB_URL || 'http://localhost:5173'}/invoice/p/${invoice.publicToken}`}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="h-4 w-4 mr-2" /> {t('detail.publicLink')}
            </a>
          </Button>
          {invoice.status === 'DRAFT' && (
            <Button size="sm" onClick={() => setSendOpen(true)}>
              <Send className="h-4 w-4 mr-2" /> {t('detail.sendInvoice')}
            </Button>
          )}
          {(invoice.status === 'PENDING' ||
            invoice.status === 'PARTIAL' ||
            invoice.status === 'OVERDUE') && (
            <Button variant="outline" size="sm" onClick={() => setReminderOpen(true)}>
              <Bell className="h-4 w-4 mr-2" /> {t('detail.sendReminder')}
            </Button>
          )}
          {(invoice.status === 'DRAFT' ||
            invoice.status === 'PENDING' ||
            invoice.status === 'PARTIAL' ||
            invoice.status === 'OVERDUE') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRecordOpen(true)}
              className="gap-1.5"
            >
              <Wallet className="w-3.5 h-3.5" />
              {t('detail.markPaid')}
            </Button>
          )}
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-2" /> {t('detail.pdf')}
          </Button>
          {invoice.status === 'DRAFT' && (
            <Button variant="outline" size="sm" disabled>
              <Edit className="h-4 w-4 mr-2" /> {t('detail.edit')}
            </Button>
          )}
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting} size="sm">
            <Trash className="h-4 w-4 mr-2" /> {t('detail.delete')}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Info Column */}
        <div className="space-y-6 md:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('detail.client')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-lg">{invoice.client?.name}</p>
              {invoice.client?.email && (
                <p className="text-sm text-muted-foreground">{invoice.client.email}</p>
              )}
              {invoice.client?.zaloPhone && (
                <p className="text-sm text-muted-foreground">{invoice.client.zaloPhone}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('detail.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('detail.created')}</span>
                <span>{new Date(invoice.createdAt).toLocaleDateString('vi-VN')}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span className="text-muted-foreground">{t('detail.dueDate')}</span>
                <span>
                  {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('vi-VN') : '-'}
                </span>
              </div>
              {invoice.publicViewedAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('detail.publicViewedAt')}</span>
                  <span>
                    {new Date(invoice.publicViewedAt).toLocaleString('vi-VN', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              )}
              {invoice.lastReminderSentAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('detail.lastReminderSentAt')}</span>
                  <span>
                    {new Date(invoice.lastReminderSentAt).toLocaleString('vi-VN', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
              )}
              <Separator className="my-2" />
              {invoice.sepayTxnId && (
                <div className="flex flex-col text-sm">
                  <span className="text-muted-foreground">{t('detail.sepayTxn')}</span>
                  <span className="font-mono text-xs break-all">{invoice.sepayTxnId}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Items Column */}
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.lineItems')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between text-sm font-medium text-muted-foreground px-2">
                  <span className="flex-1">{t('detail.description')}</span>
                  <span className="w-16 text-center">{t('detail.qty')}</span>
                  <span className="w-32 text-right">{t('detail.unitPrice')}</span>
                  <span className="w-32 text-right">{t('detail.total')}</span>
                </div>
                <Separator />
                {invoice.items?.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm px-2 py-1">
                    <span className="flex-1">{item.description}</span>
                    <span className="w-16 text-center">{item.quantity}</span>
                    <span className="w-32 text-right">{formatCurrency(item.unitPrice)}</span>
                    <span className="w-32 text-right font-medium">
                      {formatCurrency(item.totalPrice)}
                    </span>
                  </div>
                ))}

                <Separator className="my-4" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm px-2">
                    <span className="text-muted-foreground">{t('detail.subtotal')}</span>
                    <span>{formatCurrency(invoice.subTotal)}</span>
                  </div>
                  {invoice.taxRate > 0 && (
                    <div className="flex justify-between text-sm px-2">
                      <span className="text-muted-foreground">{t('detail.tax', { rate: invoice.taxRate })}</span>
                      <span>{formatCurrency(invoice.totalAmount - invoice.subTotal)}</span>
                    </div>
                  )}
                  {invoice.depositAmount > 0 && (
                    <div className="flex justify-between text-sm px-2">
                      <span className="text-muted-foreground">{t('detail.depositPaid')}</span>
                      <span>-{formatCurrency(invoice.depositAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold px-2 pt-2 border-t">
                    <span>{t('detail.balanceDue')}</span>
                    <span>{formatCurrency(invoice.totalAmount - invoice.depositAmount)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {invoice.notes && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('detail.notes')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{invoice.notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <SendInvoiceDialog
        invoice={invoice}
        mode="send"
        open={sendOpen}
        onOpenChange={setSendOpen}
      />
      <SendInvoiceDialog
        invoice={invoice}
        mode="reminder"
        open={reminderOpen}
        onOpenChange={setReminderOpen}
      />
      <RecordPaymentDrawer
        open={recordOpen}
        onOpenChange={setRecordOpen}
        mode={{
          kind: 'invoice',
          invoice: { id: invoice.id, invoiceCode: invoice.invoiceCode },
          defaultAmount: Math.max(0, Number(invoice.totalAmount) - Number(invoice.paidAmount ?? 0)),
        }}
      />
    </div>
  );
}
